/**
 * Change Role Modal
 * Modal for changing user role (user <-> admin)
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { adminService, type AdminUser } from '../../services/AdminService';
import { useAdminModalHandler } from '../../hooks/useAdminModalHandler';

interface ChangeRoleModalProps {
  user: AdminUser;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [newRole, setNewRole] = useState<'user' | 'admin'>(user.role);
  const { loading, handleSubmit } = useAdminModalHandler({ onSuccess, onClose });

  // Update role when user changes
  useEffect(() => {
    setNewRole(user.role);
  }, [user]);

  const onSubmit = () => handleSubmit(
    () => adminService.updateUserRole(user.id, newRole),
    `Updated role for ${user.username} to ${newRole}`,
    'Failed to change role'
  );

  const isRoleChanged = newRole !== user.role;
  const isPromotingToAdmin = newRole === 'admin' && user.role === 'user';
  const isDemotingFromAdmin = newRole === 'user' && user.role === 'admin';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change User Role</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* User Info */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" color="gray.600">
                User
              </Text>
              <Text fontWeight="medium">{user.username}</Text>
              <Text fontSize="sm" color="gray.500">
                {user.email}
              </Text>
            </VStack>

            {/* Current Role */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" color="gray.600">
                Current Role
              </Text>
              <Text
                fontSize="lg"
                fontWeight="bold"
                color={user.role === 'admin' ? 'purple.500' : 'blue.500'}
              >
                {user.role.toUpperCase()}
              </Text>
            </VStack>

            {/* New Role Selection */}
            <FormControl>
              <FormLabel>New Role</FormLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'user' | 'admin')}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Select>
            </FormControl>

            {/* Warning Messages */}
            {isPromotingToAdmin && (
              <Alert status="info">
                <AlertIcon />
                <VStack align="stretch" spacing={1}>
                  <AlertTitle fontSize="sm">Promoting to Admin</AlertTitle>
                  <AlertDescription fontSize="xs">
                    This user will have:
                    <br />• Access to admin panel
                    <br />• Unlimited invite generation
                    <br />• User management capabilities
                  </AlertDescription>
                </VStack>
              </Alert>
            )}

            {isDemotingFromAdmin && (
              <Alert status="warning">
                <AlertIcon />
                <VStack align="stretch" spacing={1}>
                  <AlertTitle fontSize="sm">Demoting from Admin</AlertTitle>
                  <AlertDescription fontSize="xs">
                    This user will lose:
                    <br />• Admin panel access
                    <br />• Unlimited invites (will be set to 0)
                    <br />• User management capabilities
                  </AlertDescription>
                </VStack>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme={isPromotingToAdmin ? 'purple' : isDemotingFromAdmin ? 'orange' : 'blue'}
            onClick={onSubmit}
            isLoading={loading}
            isDisabled={!isRoleChanged}
          >
            {isPromotingToAdmin ? 'Promote to Admin' : isDemotingFromAdmin ? 'Demote to User' : 'Update Role'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeRoleModal;
